import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Типы данных
export interface UserInfo {
  id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  phone_number: string | null;
  user_code: string | null;
  display_login: string | null;
  role: 'user' | 'editor' | 'admin' | 'site_admin';
  is_protected?: boolean;
  email_verified?: boolean;
  agreements_accepted?: boolean;
  has_password?: boolean;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export const SITE_OWNER_EMAILS = ['aleksandrp85@yandex.ru'];

export function isSiteOwnerEmail(email?: string | null): boolean {
  return SITE_OWNER_EMAILS.includes((email || '').trim().toLowerCase());
}

export interface AuthResponse {
  token: TokenResponse;
  user: UserInfo;
}

// ==================== Уведомления ====================

export type NotificationType = 
  | 'event_created'
  | 'event_updated'
  | 'event_cancelled'
  | 'application_approved'
  | 'application_rejected'
  | 'application_received'
  | 'new_participant'
  | 'participant_left'
  | 'free_slots'
  | 'reminder'
  | 'blog_article'
  | 'blog_comment';

export interface Notification {
  id: number;
  user_id: number;
  title: string;
  message: string;
  type: NotificationType;
  event_id: number | null;
  link: string | null;
  related_user_id: number | null;
  is_read: boolean;
  is_archived: boolean;
  created_at: string;
  read_at: string | null;
}

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
}

export interface MarkAsReadRequest {
  notification_ids?: number[];
  mark_all?: boolean;
}

// Создание axios инстанса
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Интерцептор для добавления токена
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const tokens = JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.tokens;
    
    if (tokens?.access_token) {
      config.headers.Authorization = `Bearer ${tokens.access_token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Интерцептор для обработки ошибок
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Если ошибка 401 и запрос еще не повторялся, и это НЕ auth-эндпоинт
    const isAuthEndpoint = originalRequest.url?.includes('/api/auth/login') ||
      originalRequest.url?.includes('/api/auth/register') ||
      originalRequest.url?.includes('/api/auth/check-consent');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      
      try {
        const tokens = JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.tokens;
        
        if (tokens?.refresh_token) {
          const response = await axios.post<TokenResponse>(
            `${API_BASE_URL}/api/auth/refresh`,
            { refresh_token: tokens.refresh_token }
          );
          
          // Обновляем токены в хранилище
          const newState = {
            ...JSON.parse(localStorage.getItem('auth-storage') || '{}'),
            state: {
              ...JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state,
              tokens: response.data,
            },
          };
          localStorage.setItem('auth-storage', JSON.stringify(newState));
          
          // Повторяем оригинальный запрос с новым токеном
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
          }
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Ошибка refresh токена - очищаем авторизацию
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// API сервисы
export const authAPI = {
  login: async (provider: 'google' | 'yandex' | 'vk', code: string): Promise<AuthResponse> => {
    const redirectUri = `${window.location.origin}/auth/${provider}/callback`;
    const response = await apiClient.post<AuthResponse>(`/api/auth/${provider}`, {
      code,
      redirect_uri: redirectUri,
    });
    return response.data;
  },

  emailLogin: async (email: string, password: string, options?: { consent_accepted?: boolean; captcha_token?: string }): Promise<any> => {
    const response = await apiClient.post('/api/auth/login', { email, password, ...options });
    return response.data;
  },

  checkConsent: async (email: string): Promise<{ found: boolean; agreements_accepted: boolean }> => {
    const response = await apiClient.get(`/api/auth/check-consent?email=${encodeURIComponent(email)}`);
    return response.data;
  },

  emailRegister: async (data: { email: string; password: string; first_name?: string; last_name?: string; verification_code?: string }): Promise<any> => {
    const response = await apiClient.post('/api/auth/register', data);
    return response.data;
  },

  sendVerificationCode: async (email: string, purpose?: string): Promise<{ status: string; message: string; email_sent: boolean }> => {
    const response = await apiClient.post('/api/auth/send-code', { email, purpose: purpose || 'verify' });
    return response.data;
  },

  verifyCode: async (email: string, code: string): Promise<{ status: string; message: string }> => {
    const response = await apiClient.post('/api/auth/verify-code', { email, code });
    return response.data;
  },
  
  refreshToken: async (refreshToken: string): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/api/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },
  
  getCurrentUser: async (): Promise<UserInfo> => {
    const response = await apiClient.get<UserInfo>('/api/auth/me');
    return response.data;
  },
  
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  acceptConsent: async (): Promise<{ agreements_accepted: boolean }> => {
    const response = await apiClient.post('/api/auth/accept-consent');
    return response.data;
  },
};

export interface Event {
  id: number;
  title: string;
  type_id: number;
  sport_type: string;
  sport_type_display: string;
  sport_type_icon?: string;
  start_datetime: string;
  city_id: number;
  district_id: number | null;
  metro_station_id: number | null;
  city: string;
  district: string | null;
  metro_station: string | null;
  exact_address: string | null;
  meeting_place: string | null;
  description: string | null;
  max_participants: number;
  min_participants: number;
  reserved_seats: number;
  price: number;
  is_public: boolean;
  status: 'open' | 'closed' | 'cancelled';
  created_by: number;
  created_at: string;
  updated_at: string;
  current_participants: number;
  user_role: 'guest' | 'participant' | 'organizer' | 'applicant';
  user_subscription: boolean;
  can_join: boolean;
  organizer_telegram_id?: number | null;
  organizer_username?: string | null;
  organizer_first_name?: string | null;
  organizer_last_name?: string | null;
  organizer_display_name?: string | null;
  organizer_code?: string | null;
  organizer_login?: string | null;
  applications_count?: number;
}

export interface EventsResponse {
  events: Event[];
  total: number;
  has_more: boolean;
  next_offset?: number;
}

export interface EventFilters {
  sport_type?: string;
  type_id?: number;
  city_id?: number;
  district_id?: number;
  metro_station_id?: number;
  date_from?: string;
  date_to?: string;
  time_from?: string;
  time_to?: string;
  free_slots_min?: number;
  free_slots_max?: number;
  participants_count?: string;
  price_min?: number;
  price_max?: number;
  with_free_slots?: boolean;
  is_public?: boolean;
  status_filter?: string;
  organizer_id?: number;  // Фильтр по организатору
  limit?: number;
  offset?: number;
}

export const eventsAPI = {
  getEvents: async (filters: EventFilters = {}): Promise<EventsResponse> => {
    const params = new URLSearchParams();
    
    // Добавляем user_id если есть токены
    const tokens = JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.tokens;
    if (tokens?.access_token) {
      // user_id будет получен из токена на backend
    }
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    
    const response = await apiClient.get<EventsResponse>(`/api/events?${params}`);
    return response.data;
  },
  
  getEvent: async (id: number): Promise<Event> => {
    const response = await apiClient.get<Event>(`/api/events/${id}`);
    return response.data;
  },
  
  createEvent: async (eventData: Partial<Event>, captchaToken?: string): Promise<any> => {
    const payload = captchaToken ? { ...eventData, captcha_token: captchaToken } : eventData;
    const response = await apiClient.post('/api/events/createevent', payload);
    return response.data;
  },
  
  updateEvent: async (id: number, eventData: Partial<Event>): Promise<void> => {
    await apiClient.put(`/api/events/${id}`, eventData);
  },
  
  cancelEvent: async (id: number, reason?: string): Promise<void> => {
    await apiClient.post(`/api/events/${id}/cancel`, { reason });
  },
  
  joinEvent: async (id: number, captchaToken?: string): Promise<any> => {
    const payload = captchaToken ? { captcha_token: captchaToken } : {};
    const response = await apiClient.post(`/api/events/${id}/join`, payload);
    return response.data;
  },
  
  leaveEvent: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/api/events/${id}/leave`);
    return response.data;
  },
  
  subscribeToEvent: async (id: number): Promise<{ message: string }> => {
    await apiClient.post(`/api/events/${id}/subscribe`, {
      notification_type: 'free_slot_available',
    });
    return { message: 'Подписка оформлена' };
  },
  
  unsubscribeFromEvent: async (id: number): Promise<{ message: string }> => {
    await apiClient.post(`/api/events/${id}/unsubscribe`);
    return { message: 'Подписка отменена' };
  },

  cancelApplication: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/api/events/${id}/cancel-application`);
    return response.data;
  },

  getApplications: async (eventId: number): Promise<any[]> => {
    const response = await apiClient.get(`/api/events/${eventId}/applications`);
    return response.data;
  },

  approveApplication: async (applicationId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/api/events/applications/${applicationId}/approve`);
    return response.data;
  },

  rejectApplication: async (applicationId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/api/events/applications/${applicationId}/reject`);
    return response.data;
  },
  
  getParticipants: async (eventId: number): Promise<any[]> => {
    const response = await apiClient.get(`/api/events/${eventId}/participants`);
    return response.data;
  },
  
  removeParticipant: async (eventId: number, participantId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/api/events/participants/leave`, {
      event_id: eventId,
      user_id: participantId
    });
    return response.data;
  },
  
  sendReminder: async (eventId: number, message?: string): Promise<{ message: string; stats?: any }> => {
    const response = await apiClient.post(`/api/events/${eventId}/remind`, { message });
    return response.data;
  },

  getMyApplications: async (): Promise<any[]> => {
    const response = await apiClient.get('/api/applications/my');
    return response.data;
  },

  changeEventStatus: async (eventId: number, status: 'open' | 'closed' | 'cancelled'): Promise<{ message: string }> => {
    const response = await apiClient.post(`/api/events/${eventId}/status`, { status });
    return response.data;
  },
};

export interface City {
  id: number;
  name: string;
  region: string | null;
}

export interface District {
  id: number;
  name: string;
}

export interface MetroStation {
  id: number;
  name: string;
  line_name: string;
  line_color: string;
}

export interface EventType {
  id: number;
  name: string;
  description: string | null;
  icon_emoji: string | null;
}

export const locationsAPI = {
  getCities: async (query?: string): Promise<City[]> => {
    const params = query ? `?q=${encodeURIComponent(query)}` : '';
    const response = await apiClient.get<City[]>(`/api/cities${params}`);
    return response.data;
  },
  
  getCity: async (id: number): Promise<City> => {
    const response = await apiClient.get<City>(`/api/cities/${id}`);
    return response.data;
  },
  
  getAllCities: async (): Promise<City[]> => {
    const response = await apiClient.get<City[]>('/api/cities/all');
    return response.data;
  },
  
  getDistricts: async (cityId: number, query?: string): Promise<District[]> => {
    const params = new URLSearchParams({ city_id: String(cityId) });
    if (query) params.append('q', query);
    const response = await apiClient.get<District[]>(`/api/districts?${params}`);
    return response.data;
  },
  
  getDistrict: async (id: number): Promise<District> => {
    const response = await apiClient.get<District>(`/api/districts/${id}`);
    return response.data;
  },
  
  getMetroStations: async (cityId: number, query?: string): Promise<MetroStation[]> => {
    const params = new URLSearchParams({ city_id: String(cityId) });
    if (query) params.append('q', query);
    const response = await apiClient.get<MetroStation[]>(`/api/metro-stations?${params}`);
    return response.data;
  },
  
  getEventTypes: async (): Promise<EventType[]> => {
    const response = await apiClient.get<EventType[]>('/api/event-types');
    return response.data;
  },
};

// ==================== Уведомления ====================

export const notificationsAPI = {
  getNotifications: async (
    limit: number = 50,
    offset: number = 0,
    unreadOnly: boolean = false
  ): Promise<NotificationsResponse> => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      unread_only: String(unreadOnly),
    });
    const response = await apiClient.get<NotificationsResponse>(`/api/notifications?${params}`);
    return response.data;
  },

  markAsRead: async (request: MarkAsReadRequest): Promise<{ status: string; message: string }> => {
    const response = await apiClient.post('/api/notifications/mark-as-read', request);
    return response.data;
  },

  markAsUnread: async (notificationIds: number[]): Promise<{ status: string; message: string }> => {
    const response = await apiClient.post('/api/notifications/mark-as-unread', { notification_ids: notificationIds });
    return response.data;
  },

  deleteNotification: async (notificationId: number): Promise<{ status: string; message: string }> => {
    const response = await apiClient.delete(`/api/notifications/${notificationId}`);
    return response.data;
  },

  getUnreadCount: async (): Promise<{ unread_count: number }> => {
    const response = await apiClient.get('/api/notifications?limit=1&unread_only=true');
    return { unread_count: response.data.unread_count };
  },
};

export const organizersAPI = {
  search: async (query: string): Promise<{ users: any[]; organizer_ids: number[]; error?: string }> => {
    const response = await apiClient.get(`/api/organizers/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },
};

export const profileAPI = {
  setDisplayLogin: async (display_login: string): Promise<{ status: string; message: string; display_login: string }> => {
    const response = await apiClient.put('/api/profile/login', { display_login });
    return response.data;
  },
  changePassword: async (data: { old_password: string; new_password: string; verification_code: string }): Promise<{ status: string; message: string }> => {
    const response = await apiClient.post('/api/profile/change-password', data);
    return response.data;
  },
  resetPassword: async (data: { email: string; new_password: string; verification_code: string }): Promise<{ status: string; message: string }> => {
    const response = await apiClient.post('/api/profile/reset-password', data);
    return response.data;
  },
};

export interface AdminUser {
  id: number;
  email: string | null;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  role: 'user' | 'editor' | 'admin' | 'site_admin';
  is_protected: boolean;
  email_verified: boolean;
  user_code: string;
  created_at: string;
  last_login: string | null;
  is_banned: boolean;
  ban_info: {
    id: number;
    reason: string;
    type: 'temporary' | 'permanent';
    expires_at: string | null;
    created_at: string;
  } | null;
  blog_bans: Array<{ scope: string; type: string }>;
}

export const adminAPI = {
  getUsers: async (): Promise<{ users: AdminUser[] }> => {
    const response = await apiClient.get('/api/admin/users');
    return response.data;
  },

  updateRole: async (userId: number, role: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put('/api/admin/users/role', { user_id: userId, role });
    return response.data;
  },

  banUser: async (
    userId: number,
    reason: string,
    banType: 'temporary' | 'permanent' = 'temporary',
    durationHours?: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/api/admin/users/ban', {
      user_id: userId,
      reason,
      ban_type: banType,
      duration_hours: durationHours,
    });
    return response.data;
  },

  unbanUser: async (userId: number): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/api/admin/users/unban', { user_id: userId });
    return response.data;
  },
};

export default apiClient;
